import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Users, Bot, UserPlus, Monitor, Smartphone } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RANGES = [
    { days: 1, label: "Vandaag" },
    { days: 7, label: "7 d" },
    { days: 14, label: "14 d" },
    { days: 30, label: "30 d" },
];

const dayLabel = (d) => {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit" });
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={`visitors-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <div className="flex items-center gap-2 text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-2">
            <Icon className={`h-4 w-4 ${accent || "text-[#E5A93D]"}`} /> {label}
        </div>
        <div className="font-display font-black text-3xl">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
);

export default function AdminVisitors() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(14);

    useEffect(() => {
        api.get(`/admin/visitors?days=${days}`)
            .then((r) => setData(r.data))
            .catch(() => toast.error("Bezoekersdata laden mislukt"));
    }, [days]);

    const maxSessions = useMemo(
        () => Math.max(1, ...(data?.timeline || []).map((t) => t.sessions)),
        [data]
    );

    if (!data) {
        return <div className="min-h-screen bg-zinc-50 p-10 text-sm text-zinc-500" data-testid="visitors-loading">Laden…</div>;
    }

    const t = data.totals;
    const devTotal = (data.devices.mobiel || 0) + (data.devices.desktop || 0) || 1;

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950 pb-20" data-testid="admin-visitors-page">
            <div className="max-w-6xl mx-auto px-6 pt-10">
                <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6">
                    <ArrowLeft className="h-4 w-4" /> Dashboard
                </Link>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                        <Eye className="h-6 w-6 text-[#E5A93D]" />
                        <h1 className="font-display font-black text-3xl tracking-tight">Bezoekers</h1>
                    </div>
                    <div className="flex gap-1 bg-white border border-zinc-200 rounded-full p-1">
                        {RANGES.map((r) => (
                            <button
                                key={r.days}
                                onClick={() => setDays(r.days)}
                                data-testid={`visitors-range-${r.days}`}
                                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${days === r.days ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
                <p className="text-sm text-zinc-500 mb-8">
                    Eigen meting op de website zelf (telt géén admin-pagina's). Zo zie je of advertentieklikken écht aankomen.
                </p>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard icon={Users} label="Unieke bezoekers" value={t.sessions} sub={`${t.sessions_today} vandaag`} />
                    <StatCard icon={Eye} label="Paginaweergaven" value={t.visits} />
                    <StatCard icon={Bot} label="Via ChatGPT" value={t.chatgpt_sessions} sub="unieke sessies met utm_source=chatgpt" accent="text-emerald-600" />
                    <StatCard icon={UserPlus} label="Registraties" value={t.registrations} sub={`${t.registrations_chatgpt} via ChatGPT`} accent="text-blue-600" />
                </div>

                <div className="bg-white border border-zinc-200 rounded-2xl p-6 mb-8" data-testid="visitors-chart">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-lg">Unieke bezoekers per dag</h2>
                        <div className="flex items-center gap-4 text-xs text-zinc-500">
                            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-zinc-900 inline-block" /> Alle</span>
                            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" /> ChatGPT</span>
                        </div>
                    </div>
                    <div className="flex items-end gap-[3px] h-44">
                        {data.timeline.map((d) => (
                            <div key={d.day} className="flex-1 flex flex-col justify-end items-center gap-1 group relative">
                                <div className="absolute -top-8 hidden group-hover:block bg-zinc-900 text-white text-[11px] rounded-md px-2 py-1 whitespace-nowrap z-10">
                                    {dayLabel(d.day)}: {d.sessions} bezoekers · {d.chatgpt} ChatGPT
                                </div>
                                <div className="w-full relative flex flex-col justify-end" style={{ height: "100%" }}>
                                    <div className="w-full bg-zinc-900 rounded-t-sm" style={{ height: `${(d.sessions / maxSessions) * 100}%`, minHeight: d.sessions ? 3 : 0 }} />
                                    <div className="w-full bg-emerald-500 absolute bottom-0 rounded-t-sm" style={{ height: `${(d.chatgpt / maxSessions) * 100}%`, minHeight: d.chatgpt ? 3 : 0 }} />
                                </div>
                                {data.timeline.length <= 16 && <span className="text-[10px] text-zinc-400">{dayLabel(d.day)}</span>}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="visitors-sources">
                        <h2 className="font-display font-bold text-lg mb-4">Herkomst</h2>
                        {data.sources.length === 0 && <p className="text-sm text-zinc-400">Nog geen bezoeken gemeten.</p>}
                        <div className="space-y-2.5">
                            {data.sources.map((s) => (
                                <div key={s.source} className="flex items-center justify-between text-sm">
                                    <span className={`font-medium truncate mr-3 ${s.source === "chatgpt" ? "text-emerald-700" : ""}`}>
                                        {s.source === "chatgpt" ? "🤖 ChatGPT (betaald)" : s.source}
                                    </span>
                                    <span className="text-zinc-500 shrink-0">{s.sessions} bezoekers · {s.visits} weergaven</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-6">
                        <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="visitors-pages">
                            <h2 className="font-display font-bold text-lg mb-4">Meest bezochte pagina's</h2>
                            {data.pages.length === 0 && <p className="text-sm text-zinc-400">Nog geen bezoeken gemeten.</p>}
                            <div className="space-y-2.5">
                                {data.pages.map((p) => (
                                    <div key={p.path} className="flex items-center justify-between text-sm">
                                        <span className="font-mono text-xs truncate mr-3">{p.path}</span>
                                        <span className="text-zinc-500 shrink-0">{p.visits}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="visitors-devices">
                            <h2 className="font-display font-bold text-lg mb-4">Toestel</h2>
                            <div className="flex items-center gap-6 text-sm">
                                <span className="inline-flex items-center gap-2"><Smartphone className="h-4 w-4 text-zinc-400" /> Mobiel: <b>{Math.round(((data.devices.mobiel || 0) / devTotal) * 100)}%</b></span>
                                <span className="inline-flex items-center gap-2"><Monitor className="h-4 w-4 text-zinc-400" /> Desktop: <b>{Math.round(((data.devices.desktop || 0) / devTotal) * 100)}%</b></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
